import Container from "../../../components/Container";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { IFolderRecord, INoteRecord } from "../../../lib/types";
import pb from "../../../lib/pocketbase";
import { Link, useNavigate, useParams } from "react-router-dom";
import Button from "../../../components/form/button";
import BackButton from "../../../components/form/BackButton";
import { Popover, PopoverContent, PopoverTrigger } from "@radix-ui/react-popover";
import NewFolder from "../../../components/studio/notes/NewFolder";
import NewNote from "../../../components/studio/notes/NewNote";
import Confirmation from "../../../components/form/Confirmation";
import EditFolder from "../../../components/studio/notes/EditFolder";
import FolderBreadcrumb from "../../../components/studio/notes/FolderBreadcrumb";
import Loading from "../../../components/Loading";
import { useDebounce } from "usehooks-ts";
import MoveTo from "../../../components/studio/notes/MoveTo";
import { useToast } from "../../../components/ui/use-toast";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../components/ui/tooltip";
import { config } from "../../../config";
import { createSlug } from "../../../lib/utils";

export default function Notes() {
    const folderId = useParams().folder;
    const navigate = useNavigate();
    const { toast } = useToast();

    const [loading, setIsLoading] = useState(true);
    const isLoading = useDebounce(loading, 100);
    const [folder, setFolder] = useState<IFolderRecord | undefined>(undefined);

    const [folders, setFolders] = useState<IFolderRecord[]>();
    const [notes, setNotes] = useState<INoteRecord[]>();
    const [isNewMenuOpen, setIsNewMenuOpen] = useState(false);

    async function initLoad() {
        if (!pb.authStore.isValid) return;
        setIsLoading(true);
        setFolder(undefined);
        const f: IFolderRecord[] = await pb.collection("folder").getFullList({
            filter: `parent=null&&user.id="${pb.authStore.model?.id}"`,
            sort: "name",
            expand: "parent"
        });
        setFolders(f);
        const n: INoteRecord[] = await pb.collection("note").getFullList({
            filter: `folder=null&&user.id="${pb.authStore.model?.id}"`,
            sort: "name"
        });
        setNotes(n);
        setIsLoading(false);
    }

    async function loadFolder() {
        setIsLoading(true);
        if (!folderId) {
            initLoad();
            return;
        }
        try {
            const f: IFolderRecord = await pb.collection("folder").getOne(folderId, {
                expand: "parent"
            });
            setIsLoading(false);
            setFolder(f);
        } catch (err) {
            navigate("/404");
        }
    }

    async function loadFoldersAndFiles() {
        if (folder === undefined) {
            return;
        }
        setIsLoading(true);
        const f: IFolderRecord[] = await pb.collection("folder").getFullList({
            filter: `parent.id="${folder.id}"`,
            sort: "name",
            expand: "parent"
        });
        setFolders(f);

        const n: INoteRecord[] = await pb.collection("note").getFullList({
            filter: `folder.id="${folder.id}"`,
            sort: "name"
        });
        setNotes(n);

        setIsLoading(false);
    }

    function deleteFolder(id: string) {
        pb.collection("folder").delete(id);
        setFolders((old) => old?.filter((folder) => folder.id !== id));
    }

    function deleteNote(id: string) {
        pb.collection("note").delete(id);
        setNotes((old) => old?.filter((note) => note.id !== id));
    }

    useEffect(() => {
        setIsNewMenuOpen(false);
        loadFolder();
    }, [folderId]);

    function reload() {
        if (!folder) {
            initLoad();
            return;
        }
        loadFoldersAndFiles();
    }

    useEffect(() => {
        setFolders([]);
        setNotes([]);
        loadFoldersAndFiles();
    }, [folder]);

    function changeNoteLocation(note: INoteRecord, newFolderId: string | undefined) {
        if (note.folder === newFolderId) return;
        pb.collection("note")
            .update(note.id, {
                folder: newFolderId ? newFolderId : null
            })
            .then(() => {
                setNotes((prev) => prev?.filter((n) => n.id !== note.id));
                toast({
                    title: "👍 Moved!",
                    description: "Note has been moved!"
                });
            });
    }

    function changeFolderLocation(folder: IFolderRecord, newFolderId: string | undefined) {
        if (folder.id === newFolderId) return;
        if (folder.parent === newFolderId) return;
        pb.collection("folder")
            .update(folder.id, {
                parent: newFolderId ? newFolderId : null
            })
            .then(() => {
                setFolders((prev) => prev?.filter((f) => f.id !== folder.id));
                toast({
                    title: "👍 Moved!",
                    description: "Folder has been moved!"
                });
            });
    }

    function copyFolderUrl(folder: IFolderRecord) {
        navigator.clipboard?.writeText(`${config.base}/folder/${createSlug(folder.name)}/${folder.id}`);
        toast({
            title: "🤩 Successfully copied url!",
            description: "You have successfully copied the url of this folder."
        });
    }

    function copyNoteUrl(note: INoteRecord) {
        navigator.clipboard?.writeText(`${config.base}/note/${createSlug(note.name)}/${note.id}`);
        toast({
            title: "🤩 Successfully copied url!",
            description: "You have successfully copied the url of this note."
        });
    }

    return (
        <>
            <Helmet>
                <title>ReinVB Studio — Notes</title>
            </Helmet>
            <Container className="my-8 lg:my-16">
                <div className="flex items-center gap-4">
                    <div className="flex-grow">{folder !== undefined ? <FolderBreadcrumb folder={folder} /> : <h2 className="text-3xl font-bold">Notes.</h2>}</div>
                    <Popover open={isNewMenuOpen} onOpenChange={setIsNewMenuOpen}>
                        <PopoverTrigger className="flex items-center">
                            <Button size={"box"}>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className="h-4 w-4">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                </svg>
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent align="end" className="absolute right-0 z-30 mt-2 w-64 overflow-hidden rounded-lg border bg-white dark:bg-slate-900">
                            <div className="flex flex-col">
                                <NewFolder parent={folder} />
                                <NewNote folder={folder} />
                            </div>
                        </PopoverContent>
                    </Popover>
                </div>

                <div className="mt-8 flex flex-col gap-4">
                    {!loading && folders?.length === 0 && notes?.length === 0 && <div className="text-sm italic text-slate-500 dark:text-slate-600">This folder is still empty, add something using the +</div>}
                    {isLoading && (
                        <div className="flex justify-center">
                            <Loading />
                        </div>
                    )}
                    {!isLoading &&
                        folders?.map((folder) => (
                            <div className="relative" key={folder.id}>
                                <Link key={folder.id} to={`/studio/notes/${folder.id}`}>
                                    <Button variant={"ghost"} size={"ghost"} className="flex w-full items-center justify-between rounded-lg bg-slate-100 px-6 py-4 active:scale-95 dark:bg-slate-800">
                                        <div className="flex items-center gap-3">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5">
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z"
                                                />
                                            </svg>
                                            <span className="font-semibold">{folder.name}</span>
                                            {folder.public && (
                                                <TooltipProvider>
                                                    <Tooltip>
                                                        <TooltipTrigger>
                                                            <div className="h-2 w-2 rounded-full bg-blue-400"></div>
                                                        </TooltipTrigger>
                                                        <TooltipContent>Public</TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            )}
                                        </div>
                                    </Button>
                                </Link>

                                <Popover>
                                    <PopoverTrigger className="absolute right-3 top-3">
                                        <Button variant={"outline"} size={"box"} className="">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z" />
                                            </svg>
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent align="end" className="absolute right-0 z-30 mt-1 w-48 overflow-hidden rounded-lg border bg-white dark:bg-slate-900">
                                        <div className="flex flex-col py-2">
                                            <Link to={`/studio/notes/${folder.id}`} className="flex cursor-pointer items-center gap-3 px-4 py-2 hover:opacity-80">
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                </svg>
                                                Open
                                            </Link>
                                            <EditFolder folder={folder} reload={() => reload()} />
                                            <MoveTo movingFolder={folder.id} folder={folder.expand.parent} onSubmit={(id) => changeFolderLocation(folder, id)} folders={folders} />
                                            {folder.public && (
                                                <button onClick={() => copyFolderUrl(folder)} className="flex cursor-pointer items-center gap-3 px-4 py-2 hover:opacity-80">
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
                                                    </svg>
                                                    Copy url
                                                </button>
                                            )}
                                            <Confirmation title={`Are you sure?`} description={`Are you sure you want to delete this folder (${folder.name}), this will also delete everything inside.`} onConfirm={() => deleteFolder(folder.id)}>
                                                <div className="flex cursor-pointer items-center gap-3 px-4 py-2 hover:opacity-80">
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                                                        />
                                                    </svg>
                                                    Delete
                                                </div>
                                            </Confirmation>
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            </div>
                        ))}
                    {!isLoading &&
                        notes?.map((note) => (
                            <div className="relative" key={note.id}>
                                <Link key={note.id} to={`/studio/notes/note/${note.id}`}>
                                    <Button variant={"ghost"} size={"ghost"} className="flex w-full items-center justify-between rounded-lg border px-6 py-4 active:scale-95">
                                        <div className="flex items-center gap-3">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                                                />
                                            </svg>

                                            <span className="font-semibold">{note.name}</span>
                                            {note.public && (
                                                <TooltipProvider>
                                                    <Tooltip>
                                                        <TooltipTrigger>
                                                            <div className="h-2 w-2 rounded-full bg-blue-400"></div>
                                                        </TooltipTrigger>
                                                        <TooltipContent>Public</TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            )}
                                        </div>
                                    </Button>
                                </Link>
                                <Popover>
                                    <PopoverTrigger className="absolute right-3 top-3">
                                        <Button variant={"outline"} size={"box"} className="">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z" />
                                            </svg>
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent align="end" className="absolute right-0 z-30 mt-1 w-48 overflow-hidden rounded-lg border bg-white dark:bg-slate-900">
                                        <div className="flex flex-col py-2">
                                            <Link to={`/studio/notes/note/${note.id}`} className="flex cursor-pointer items-center gap-3 px-4 py-2 hover:opacity-80">
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                </svg>
                                                Open
                                            </Link>
                                            <Link to={`/studio/notes/note/${note.id}/edit`} className="flex cursor-pointer items-center gap-3 px-4 py-2 hover:opacity-80">
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
                                                </svg>
                                                Edit
                                            </Link>
                                            <MoveTo folder={folder} onSubmit={(id) => changeNoteLocation(note, id)} folders={folders} />
                                            {note.public && (
                                                <button onClick={() => copyNoteUrl(note)} className="flex cursor-pointer items-center gap-3 px-4 py-2 hover:opacity-80">
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
                                                    </svg>
                                                    Copy url
                                                </button>
                                            )}
                                            <Confirmation title={`Are you sure?`} description={`Are you sure you want to delete this note (${note.name})?`} onConfirm={() => deleteNote(note.id)}>
                                                <div className="flex cursor-pointer items-center gap-3 px-4 py-2 hover:opacity-80">
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-4 w-4">
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                                                        />
                                                    </svg>
                                                    Delete
                                                </div>
                                            </Confirmation>
                                        </div>
                                    </PopoverContent>
                                </Popover>
                            </div>
                        ))}
                </div>
            </Container>
        </>
    );
}
